"use client"

import { useState } from "react"
import { EmphasisInHeading } from "~/components/ui/EmphasisInHeading"
import { IconChip } from "~/components/ui/IconChip"
import { Section } from "~/components/ui/Section"
import { Text } from "~/components/ui/Text"
import { css, cx } from "~/generated/styled-system/css"
import { eyebrow, heading } from "~/generated/styled-system/recipes"
import faqsData from "./faqs.json"

type Faq = { q: string; a: string }

// PLACEHOLDER COPY — Linda to confirm/edit in faqs.json.
const FAQS: readonly Faq[] = faqsData

const reducedMotion = { "@media (prefers-reduced-motion: reduce)": { transitionDuration: "0s" } } as const

const triggerClass = css({
  all: "unset",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "6",
  width: "full",
  minHeight: "11",
  paddingBlock: "5",
  paddingInline: "1",
  cursor: "pointer",
  color: "colorPalette.ink",
  transitionProperty: "color",
  transitionDuration: "normal",
  focusVisibleRing: "outside",
  _hover: { color: "colorPalette.accent" },
  ...reducedMotion
})

// Type lives on the inner span: the trigger's `all: unset` is a utility, which outranks the `compositions` layer that
// text styles compile into, so a `textStyle` on the button itself would be wiped.
const questionTextClass = css({
  // A heading role (the question sits inside an h3): Kaisei medium like every heading.
  fontFamily: "display",
  textStyle: "text.lg",
  fontWeight: 500,
  lineHeight: "1.4"
})

const iconGlyphClass = css({
  transitionProperty: "transform, background, color, border-color",
  transitionDuration: "slow",
  "&[data-open]": { transform: "rotate(45deg)" },
  _before: {
    content: "\"\"",
    position: "absolute",
    width: "3",
    height: "1.5px",
    bg: "currentColor"
  },
  _after: {
    content: "\"\"",
    position: "absolute",
    width: "3",
    height: "1.5px",
    bg: "currentColor",
    transform: "rotate(90deg)"
  },
  ...reducedMotion
})

// Grid-rows 0fr → 1fr animates to the content's real height (no max-height clipping).
// `visibility` keeps a closed answer out of the accessibility tree; it's in the
// transition list so it stays visible through the closing animation.
const panelClass = css({
  display: "grid",
  gridTemplateRows: "0fr",
  visibility: "hidden",
  transitionProperty: "grid-template-rows, visibility",
  transitionDuration: "360ms",
  transitionTimingFunction: "settle",
  "&[data-open]": { gridTemplateRows: "1fr", visibility: "visible" },
  ...reducedMotion
})

const answerClass = css({
  paddingBlockEnd: "6",
  paddingInline: "1",
  maxInlineSize: "68ch"
})

function FaqItem({ q, a, index }: Faq & { index: number }) {
  const [open, setOpen] = useState(false)
  const id = `faq-panel-${index}`
  const triggerId = `faq-trigger-${index}`
  return (
    <div className={css({ borderBlockEnd: "1px solid", borderBlockEndColor: "colorPalette.divider" })}>
      <h3>
        <button
          type="button"
          id={triggerId}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((v) => !v)}
          className={triggerClass}
        >
          <span className={questionTextClass}>{q}</span>
          <IconChip
            aria-hidden="true"
            size="sm"
            variant={open ? "solid" : "outline"}
            tone={open ? "primary" : undefined}
            data-open={open || undefined}
            className={iconGlyphClass}
          />
        </button>
      </h3>
      <div id={id} role="region" aria-labelledby={triggerId} data-open={open || undefined} className={panelClass}>
        <div className={css({ overflow: "hidden", minHeight: 0 })}>
          <Text size="md" variant="muted" className={answerClass}>
            {a}
          </Text>
        </div>
      </div>
    </div>
  )
}

export function FaqSection() {
  return (
    <Section
      tone="neutral"
      variant="alt"
      size="md"
      id="faq"
      aria-labelledby="faq-heading"
      // Contact (white) sits directly above; the hairline keeps the two grounds distinct (DESIGN.md §6.4).
      className={css({ borderBlockStart: "1px solid", borderBlockStartColor: "colorPalette.divider" })}
    >
      <div className={css({ maxInlineSize: "4xl", marginInline: "auto", display: "grid", gap: "10" })}>
        <div className={css({ textAlign: "center" })}>
          <p className={cx(eyebrow(), css({ marginBlockEnd: "3" }))}>Common questions</p>
          <h2 id="faq-heading" className={heading({ size: "md" })}>
            A few things people often{" "}
            <EmphasisInHeading>
              ask
            </EmphasisInHeading>
            .
          </h2>
        </div>

        <div className={css({ borderBlockStart: "1px solid", borderBlockStartColor: "colorPalette.divider" })}>
          {FAQS.map((item, i) => <FaqItem key={i} q={item.q} a={item.a} index={i} />)}
        </div>
      </div>
    </Section>
  )
}
